/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {
  useQuery,
  useQueryClient,
  UseQueryResult
} from 'react-query';
import {
  DevicePlatformQueryParams,
  getDevicePlatforms
} from '@ion/services/src/graphql/GraphQL';
import {Platform} from '@ion/services/src/graphql/types';
import {createKey, QueryKey} from '../../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../../utils/helpers';

export type UsePlatformsResult = {
  errorResult: UseQueryResult,
  query: UseQueryResult,
  platforms: Platform[],
  isFetching: boolean,
  isFetched: boolean,
  isError: boolean,
  statusCode: number,
  resetError: () => void;
};

export const usePlatforms= (
  params: DevicePlatformQueryParams,
  enabled = true,
  staleTime = getStaleTime(1, true, false)
): UsePlatformsResult => {
  const queryClient = useQueryClient();

  const key = useMemo(() => (
    createKey(QueryKey.PLATFORMS, params)
  ), [params]);

  const query = useQuery(key, async() => {
    const platforms = await getDevicePlatforms(params);
    return platforms;
  }, {
    enabled,
    staleTime : staleTime as number,
    retry     : (failureCount, error) => {
      if (error?.statusCode === 200) {
        return false;
      }
      return failureCount < 3;
    }
  });

  const resetError = () => {
    queryClient.setQueryData(key, (data: any) => ({
      ...data,
      isError : false,
      error   : null
    }));
  };

  return createQueryResult(query, {
    errorResult : query?.error?.results,
    isError     : query?.isError,
    isFetched   : query?.isFetched,
    platforms   : query.data || [],
    isFetching  : query.isFetching,
    statusCode  : query?.error?.statusCode,
    resetError
  }) as UsePlatformsResult;
};

export default usePlatforms;
